import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import clsx from 'clsx';
import { AlertCircle, AlertTriangle, CheckCircle2, ScanSearch, X } from 'lucide-react';
import { Button } from '../../../components/ui/Button';
import { signalDismissed } from '../../../features/operator/operatorDeviceSlice';
import {
  bandActionOf,
  retryActionOf,
  selectBandAction,
  selectBandActionStatus,
  selectBandRetry,
  selectBandRetryBusy,
} from '../operatorRetry';
import { CodeText } from './CodeText';

const TONES = {
  success: { icon: CheckCircle2, className: 'bg-ninja-green text-white' },
  error: { icon: AlertCircle, className: 'bg-danger text-white' },
  warning: { icon: AlertTriangle, className: 'bg-amber-400 text-ninja-dark' },
  step: { icon: ScanSearch, className: 'bg-ninja-dark text-white dark:bg-[#2d2d2d]' },
};

/**
 * How the last action went, under the top bar: big, in a solid colour that
 * reads at arm's length, with the same tone the phone just played. A write
 * whose answer was lost offers to send it again, where it can go from; a
 * take offers «Deshacer» while it is the last. It only shows:
 * `OperatorLiveRegion` says it to screen readers.
 */
export const FeedbackBand = () => {
  const dispatch = useDispatch();
  const signal = useSelector((state) => state.operatorDevice.signal);
  const retry = useSelector(selectBandRetry);
  const retrying = useSelector(selectBandRetryBusy);
  const action = useSelector(selectBandAction);
  const actionStatus = useSelector(selectBandActionStatus);
  if (!signal) return null;

  const tone = TONES[signal.tone] || TONES.step;
  const Icon = tone.icon;

  return (
    <div
      data-testid="feedback-band"
      data-tone={signal.tone}
      className={clsx('flex min-h-[56px] items-center gap-3 px-4 py-2', tone.className)}
    >
      <Icon size={22} className="shrink-0" />
      <p className="flex-1 text-base font-semibold leading-snug">
        <CodeText text={signal.message} />
      </p>
      {retry && (
        <Button
          variant="secondary"
          size="lg"
          loading={retrying}
          loadingLabel="Reintentando…"
          onClick={() => dispatch(retryActionOf(retry))}
          data-testid="feedback-band-retry"
        >
          Reintentar
        </Button>
      )}
      {action && (
        <Button
          variant="secondary"
          size="lg"
          loading={actionStatus === 'busy'}
          loadingLabel={action.busyLabel}
          disabled={actionStatus === 'blocked'}
          onClick={() => dispatch(bandActionOf(action))}
          data-testid={action.testId}
        >
          {action.label}
        </Button>
      )}
      {/* Not `subtle`: its grey does not read on the band's colours. */}
      <Button
        variant="secondary"
        size="lg"
        iconOnly
        icon={X}
        aria-label="Cerrar aviso"
        onClick={() => dispatch(signalDismissed())}
        data-testid="feedback-band-dismiss"
        className="shrink-0"
      />
    </div>
  );
};
